import { clearAnalyticsRegistrationMeta } from '../analytics/registrationMeta';


export type PartnerInfo = {
    email?: string;
    phone?: string;
    docType?: number;
    docNumber?: string;
    partner?: string;
    partnerRedirectUrl?: string;
    partnerInfo?: Record<string, unknown>;
    [key: string]: unknown;
};

export type AccountInfo = Record<string, unknown>;

const STORAGE_KEYS = {
    accountId: 'bioweb_account_id',
    partner: 'bioweb_partner',
    account: 'bioweb_account',
    accountInfo: 'bioweb_account_info',
} as const;

let partner: PartnerInfo | null = null;
let accountId: string | null = null;
let account: AccountInfo | null = null;
let accountInfo: AccountInfo | null = null;
let error: string | null = null;

function readJson<T>(key: string): T | null {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
    } catch {
        return null;
    }
}

function writeJson(key: string, value: unknown) {
    if (value == null) {
        sessionStorage.removeItem(key);
        return;
    }
    sessionStorage.setItem(key, JSON.stringify(value));
}

function hydrateFromSession() {
    const storedAccountId = sessionStorage.getItem(STORAGE_KEYS.accountId);
    accountId = storedAccountId || null;
    partner = readJson<PartnerInfo>(STORAGE_KEYS.partner);
    account = readJson<AccountInfo>(STORAGE_KEYS.account);
    accountInfo = readJson<AccountInfo>(STORAGE_KEYS.accountInfo);
}

hydrateFromSession();

const listeners = new Set<() => void>();

export function subscribeRegistrationState(listener: () => void) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function notifyRegistrationState() {
    listeners.forEach((listener) => listener());
}

export const clearRegistrationState = () => {
    partner = null;
    accountId = null;
    account = null;
    accountInfo = null;
    error = null;
    sessionStorage.removeItem(STORAGE_KEYS.accountId);
    sessionStorage.removeItem(STORAGE_KEYS.partner);
    sessionStorage.removeItem(STORAGE_KEYS.account);
    sessionStorage.removeItem(STORAGE_KEYS.accountInfo);
    clearAnalyticsRegistrationMeta();
    notifyRegistrationState();
};

export const setPartnerInfo = (_partner: PartnerInfo | null) => {
    partner = _partner;
    writeJson(STORAGE_KEYS.partner, _partner);
    notifyRegistrationState();
};

export const getPartner = () => partner;

export const isPartner = () => Boolean(partner);

export const setAccountId = (_accountId: string | number | null) => {
    accountId = _accountId != null ? String(_accountId) : null;
    if (_accountId != null) {
        sessionStorage.setItem(STORAGE_KEYS.accountId, String(_accountId));
    } else {
        sessionStorage.removeItem(STORAGE_KEYS.accountId);
    }
    notifyRegistrationState();
};

export const getAccountId = () => accountId;

export const setAccount = (_account: AccountInfo | null) => {
    account = _account;
    writeJson(STORAGE_KEYS.account, _account);
    notifyRegistrationState();
};

export const getAccount = () => account;

export const setAccountInfo = (_accountInfo: AccountInfo | null) => {
    accountInfo = _accountInfo;
    writeJson(STORAGE_KEYS.accountInfo, _accountInfo);
    notifyRegistrationState();
};

export const getAccountInfo = () => accountInfo;

export const setError = (_error: string | null) => {
    error = _error;
};

export const getError = () => (error !== null ? error : undefined);
